// Eigenbau: GitLab-Lieferung im Projektdetail (Paket 12) — erweitert um
// MR-Details (Commits, Diffs), Pipeline-Jobs (Paket 17) und die
// Inline-Verknüpfung eines GitLab-Projekts direkt aus dem Leerzustand.
// In der Form der Vorlage: Titel in 46, der GitLab-Kopf als Zeile über
// dem Zahlenband, die Bereiche als Reiter mit Tabmarke.
import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { istPipelineOffen } from "@cubic/shared";
import { api } from "@/lib/api";
import { schluessel } from "@/lib/abfragen";
import { useCursorListe, useProjekt } from "@/lib/haken";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import metarrGitlab from "@/bilder/metarr-gitlab-384.png";
import { MrAbschnitt } from "./lieferung/MrAbschnitt";
import { PipelineAbschnitt } from "./lieferung/PipelineAbschnitt";
import { PaginierterBereich } from "@/components/Paginierung";
import { MehrLaden } from "@/components/MehrLaden";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import { Seitenkopf } from "@/components/entwurf/Seitenkopf";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Kennzahl } from "@/components/entwurf/Kennzahl";
import { Zahlenband, Nebenwert } from "@/components/entwurf/Zahlenband";
import { Reiter, Reiterleiste } from "@/components/entwurf/Reiter";
import { Leerzustand } from "@/components/Leerzustand";
import { qualitaetsGateLaeuft } from "@/lib/qualitaets-gate-zustand";
import { cx, focusRing } from "@/lib/utils";

type LieferungBereich =
  | "merge_requests"
  | "pipelines"
  | "commits"
  | "auslieferungen"
  | "unzugeordnete";

function Fehler({
  text,
  erneut,
}: {
  text: string;
  erneut: () => void;
}) {
  return (
    <div
      className="space-y-2 rounded-lg bg-red-50 px-4 py-3 dark:bg-red-950/40"
      role="alert"
    >
      <p className="text-sm text-red-700 dark:text-red-400">{text}</p>
      <Button variant="secondary" onClick={erneut}>
        Erneut versuchen
      </Button>
    </div>
  );
}

/**
 * Leerzustand eines Abschnitts.
 *
 * Als nackter grauer Satz war nicht zu unterscheiden, ob ein Abschnitt leer ist
 * oder gerade nichts geladen hat — beides sah aus wie ein verirrter Halbsatz.
 * Die Abgrenzung kommt aus der Fläche, nicht aus einem Strich: ein Rahmen um
 * einen einzelnen Satz macht aus einem Hinweis eine zweite Karte.
 */
function Leer({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg bg-gray-50 px-4 py-6 text-center text-sm text-gray-500 dark:bg-gray-900 dark:text-gray-400">
      {children}
    </p>
  );
}

/** Ladezustand — gleiche Fläche und Höhe wie der Leerzustand, nichts springt. */
function Laedt({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg bg-gray-50 px-4 py-6 text-center text-sm text-gray-400 dark:bg-gray-900 dark:text-gray-500">
      {children}
    </p>
  );
}

/**
 * Der Abschnitt besitzt seinen eigenen Abstand — im neuen Reiter entfallen
 * die alten Abschnittsüberschriften; die Bereiche sind Reiter geworden.
 */

function formatiereZeitpunkt(wert: string): string {
  return new Date(wert).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Eine Commit-Zeile, die sich aufklappen lässt.
 *
 * Zugeklappt bleibt es bei der Kurzfassung: acht Zeichen SHA, die erste Zeile
 * der Nachricht, Autor und Zeitpunkt. Das reicht zum Überfliegen und hält die
 * Liste ruhig.
 *
 * Aufgeklappt steht alles da, was gespeichert ist. Wichtig ist dabei die
 * **vollständige Nachricht**: Ein Commit
 * trägt Betreff und Rumpf, und der Rumpf ist oft das Interessante. Die
 * einzeilige Fassung schnitt ihn wortlos ab.
 */
function CommitZeile({
  commit,
  mergeRequestIid,
}: {
  commit: {
    id: string;
    sha: string;
    nachricht: string;
    autor: string;
    geschehenAm: string;
    mergeRequestId: string | null;
  };
  mergeRequestIid: number | null;
}) {
  const [offen, setzeOffen] = useState(false);
  /* Betreff und Rumpf trennen — Git trennt sie durch eine Leerzeile. */
  const [betreff, ...rumpf] = commit.nachricht.split("\n");
  const hatRumpf = rumpf.join("\n").trim().length > 0;

  return (
    <li className="border-b border-linie">
      <button
        type="button"
        aria-expanded={offen}
        onClick={() => setzeOffen((vorher) => !vorher)}
        className={cx(
          "flex w-full flex-wrap items-center gap-3.5 py-[15px] text-left",
          focusRing,
        )}
      >
        <span
          aria-hidden="true"
          className={cx(
            "font-mono text-[10px] text-tinte4 transition-transform duration-150 ease-aus",
            offen ? "rotate-90" : "",
          )}
        >
          ▶
        </span>
        <span className="font-mono text-[11.5px] tabular-nums text-tinte4">
          {commit.sha.slice(0, 8)}
        </span>
        <span className="min-w-0 flex-1 truncate text-[13.5px] text-tinte">
          {betreff}
        </span>
        <Mikrolabel grad={11} className="flex-none">
          {commit.autor || ""}
          {commit.autor ? " · " : ""}
          <span className="tabular-nums">
            {formatiereZeitpunkt(commit.geschehenAm)}
          </span>
        </Mikrolabel>
      </button>

      {offen ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 pb-5 pl-[26px] pr-2 text-[12.5px]">
          <dt className="font-mono text-[11px] text-tinte4">SHA</dt>
          <dd className="break-all font-mono text-[11.5px] text-tinte2">
            {commit.sha}
          </dd>

          <dt className="font-mono text-[11px] text-tinte4">Autor</dt>
          <dd className="text-tinte2">{commit.autor || "—"}</dd>

          <dt className="font-mono text-[11px] text-tinte4">Zeitpunkt</dt>
          <dd className="tabular-nums text-tinte2">
            {formatiereZeitpunkt(commit.geschehenAm)}
          </dd>

          <dt className="font-mono text-[11px] text-tinte4">Merge Request</dt>
          <dd className="font-mono text-[11.5px] text-tinte2">
            {mergeRequestIid !== null ? `!${mergeRequestIid}` : "keiner"}
          </dd>

          <dt className="font-mono text-[11px] text-tinte4">Nachricht</dt>
          <dd className="whitespace-pre-wrap text-tinte2">
            {hatRumpf ? commit.nachricht : betreff}
          </dd>
        </dl>
      ) : null}
    </li>
  );
}

export function LieferungReiter({
  projektId,
  hatGitlab,
  bereich,
}: {
  projektId: string;
  hatGitlab: boolean;
  bereich?: LieferungBereich;
}) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [gitlabPfad, setzeGitlabPfad] = useState("");
  const [fehler, setzeFehler] = useState<string | null>(null);
  const projekt = useProjekt(projektId);

  const verknuepfen = useMutation({
    mutationFn: (pfad: string) =>
      api.gitlab.setzeProjekt.mutate({ projektId, gitlabProjekt: pfad }),
    onSuccess: () => {
      setzeGitlabPfad("");
      setzeFehler(null);
      void client.invalidateQueries({ queryKey: schluessel.projekt(projektId) });
    },
    onError: (e) => {
      const msg =
        e instanceof Error ? e.message : "Verknüpfung fehlgeschlagen.";
      setzeFehler(msg);
    },
  });

  const mrs = useCursorListe({
    queryKey: schluessel.gitlabMrs(projektId),
    lade: (cursor) =>
      api.gitlab.mergeRequests.query({ projektId, cursor, grenze: 50 }),
    enabled: Boolean(projektId) && hatGitlab,
    aktualisierung: {
      alleMs: 5_000,
      solange: (eintraege) =>
        eintraege.some((mr) =>
          qualitaetsGateLaeuft(mr.qualitaetsGate.gruende),
        ),
    },
  });
  const pipelines = useCursorListe({
    queryKey: schluessel.gitlabPipelines(projektId),
    lade: (cursor) =>
      api.gitlab.pipelines.query({ projektId, cursor, grenze: 50 }),
    enabled: Boolean(projektId) && hatGitlab,
    /*
     * Nachgefragt wird nur, solange etwas offen ist. Ohne das blieb ein Lauf
     * für immer „Läuft", auch wenn er längst durch war — die Seite fragte
     * überhaupt nicht nach, und nur ein Neuladen brachte den Stand.
     *
     * `refetchIntervalInBackground: false`: Ein Reiter im Hintergrund muss
     * niemanden fragen; er zeigt ohnehin niemandem etwas.
     */
    /*
       Dieselbe Wahrheit wie im Nachzug: `istPipelineOffen` aus shared. Vorher
       stand hier eine eigene Liste, der `canceling` und `manual` fehlten — zwei
       Orte mit zwei Antworten auf dieselbe Frage.
     */
    aktualisierung: {
      alleMs: 5_000,
      solange: (eintraege) =>
        eintraege.some((lauf) => istPipelineOffen(lauf.zustand)),
    },
  });
  const commits = useCursorListe({
    queryKey: schluessel.gitlabCommits(projektId),
    lade: (cursor) =>
      api.gitlab.commits.query({ projektId, cursor, grenze: 50 }),
    enabled: Boolean(projektId) && hatGitlab,
  });
  const auslieferungen = useCursorListe({
    queryKey: schluessel.gitlabAuslieferungen(projektId),
    lade: (cursor) =>
      api.gitlab.auslieferungen.query({ projektId, cursor, grenze: 50 }),
    enabled: Boolean(projektId) && hatGitlab,
  });
  const unzugeordnete = useCursorListe({
    queryKey: schluessel.gitlabUnzugeordnete(projektId),
    lade: (cursor) =>
      api.gitlab.unzugeordnete.query({ projektId, cursor, grenze: 50 }),
    enabled: Boolean(projektId) && hatGitlab,
  });
  /*
   * Der offene Bereich steht in der Adresse, nicht in einem `useState`.
   *
   * Vorher las ihn ein Zustand einmal beim Einhängen aus `bereich` und blieb
   * danach stehen. Der Verweis „Pipelines öffnen" im Quality-Gate steht selbst
   * innerhalb dieses Reiters: Er änderte die Adresse, traf aber eine längst
   * eingehängte Komponente — sichtbar geschah nichts. Aus der Adresse
   * abgeleitet wirkt er, und ein Bereich lässt sich verlinken und merken.
   *
   * `replace` hält die Verlaufsliste frei: Ein Reiterwechsel ist keine neue
   * Station, und der Zurück-Knopf führt aus der Seite heraus statt durch fünf
   * Bereiche zurück.
   */
  const aktiverBereich: LieferungBereich = bereich ?? "merge_requests";
  const setzeAktivenBereich = (name: LieferungBereich) => {
    void navigate({
      to: "/projekte/$id",
      params: { id: projektId },
      search: (alt) => ({ ...alt, reiter: "lieferung", lieferung: name }),
      replace: true,
    });
  };

  // GitLab-Projekte für das Verknüpfungs-Dropdown (nur ohne Verknüpfung).
  const projekteSuchen = useQuery({
    queryKey: schluessel.gitlabProjekteSuchen(projektId),
    queryFn: () => api.gitlab.projekteSuchen.query({ projektId }),
    enabled: Boolean(projektId) && !hatGitlab,
  });
  const waehlbareProjekte =
    projekteSuchen.data?.eingerichtet === true
      ? projekteSuchen.data.projekte
      : [];

  const handleVerknuepfen = (e: FormEvent) => {
    e.preventDefault();
    if (!gitlabPfad.trim()) return;
    verknuepfen.mutate(gitlabPfad.trim());
  };

  if (!hatGitlab) {
    return (
      <Leerzustand
        titel="GitLab mit diesem Projekt verknüpfen."
        hinweis="Wähle das GitLab-Projekt aus der Liste oder trage den Pfad manuell ein (z. B. gruppe/projekt), damit Merge Requests und Pipelines automatisch zugeordnet werden."
        /*
          Hier steht Metarr mit dem GitLab-Zeichen statt des allgemeinen
          Avatars: Der Leerzustand meint genau diese eine Sache, und das Bild
          sagt sie, bevor die Überschrift gelesen ist.
        */
        bild={
          <img
            src={metarrGitlab}
            alt=""
            width={384}
            height={384}
            className="mb-2 h-auto w-[132px]"
            draggable={false}
          />
        }
        aktion={
          <form
            onSubmit={handleVerknuepfen}
            className="mx-auto max-w-md space-y-4 text-left"
          >
            {waehlbareProjekte.length > 0 ? (
            <div>
              <Label htmlFor="gitlab-projekt-auswahl">GitLab-Projekt</Label>
              <Select
                value={gitlabPfad}
                onValueChange={(wert) => {
                  setzeGitlabPfad(wert);
                  if (fehler) setzeFehler(null);
                }}
              >
                <SelectTrigger
                  id="gitlab-projekt-auswahl"
                  className="mt-1.5 w-full"
                  aria-label="GitLab-Projekt auswählen"
                >
                  <SelectValue placeholder="GitLab-Projekt wählen …" />
                </SelectTrigger>
                <SelectContent>
                  {waehlbareProjekte.map((projekt) => (
                    <SelectItem key={projekt.pfad} value={projekt.pfad}>
                      <span className="flex items-center gap-2">
                        <span>{projekt.name ?? projekt.pfad}</span>
                        <span className="font-mono text-xs text-gray-400">
                          {projekt.pfad}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label htmlFor="gitlab-projekt-pfad">GitLab-Projektpfad</Label>
              <Input
                id="gitlab-projekt-pfad"
                className="mt-1.5"
                placeholder="gruppe/projekt"
                value={gitlabPfad}
                maxLength={255}
                onChange={(e) => {
                  setzeGitlabPfad(e.target.value);
                  if (fehler) setzeFehler(null);
                }}
              />
              {projekteSuchen.data?.eingerichtet === false &&
              !projekteSuchen.isLoading ? (
                <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                  Die Projektliste ist nicht verfügbar — auf dem Server fehlen
                  GITLAB_HOST und GITLAB_TOKEN. Der Pfad kann trotzdem manuell
                  eingetragen werden.
                </p>
              ) : null}
            </div>
          )}
          {fehler ? (
            <p className="text-sm text-red-600" role="alert">
              {fehler}
            </p>
          ) : null}
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={!gitlabPfad.trim() || verknuepfen.isPending}
              isLoading={verknuepfen.isPending}
              loadingText="Verknüpfe …"
            >
              Verknüpfen
            </Button>
            <span className="self-center text-xs text-gray-400">
              Benötigt das Recht „gitlab.konfigurieren“.
            </span>
          </div>
          </form>
        }
      >
      </Leerzustand>
    );
  }
  const offeneMrs = mrs.eintraege.filter(
    (m) => m.zustand === "opened" || m.zustand === "open",
  ).length;
  const fehlgeschlagene = pipelines.eintraege.filter(
    (p) => p.zustand === "failed",
  ).length;

  const BEREICH_LABEL: Record<LieferungBereich, string> = {
    merge_requests: "Merge Requests",
    pipelines: "Pipelines",
    commits: "Commits",
    auslieferungen: "Auslieferungen",
    unzugeordnete: "Unzugeordnete",
  };

  return (
    <div data-testid="projekt-lieferung">
      {/*
        GitLab-Verknüpfung und Abholen sitzen im Seitenkopf: die Angabe als
        Kontextzeile, der Knopf als Aktion. Vorher stand darüber eine eigene
        Zeile mit Knopf, und die schob den Titel gegenüber allen anderen Seiten
        nach unten.
      */}
      <Seitenkopf
        kontext={
          <>
            GitLab{" "}
            <span className="text-tinte">
              {projekt.data?.gitlabProjekt ?? "—"}
            </span>{" "}
            · verbunden
          </>
        }
        /*
         * Hier stand „Verknüpfung entfernen". Der Knopf ist nach
         * Einstellungen → GitLab gezogen: Die Lieferung ist die Arbeitsansicht, und das Lösen der
         * Verknüpfung stoppt jeden weiteren Zufluss für das ganze Projekt —
         * das gehört dorthin, wo man es sucht, wenn man es will, und nicht
         * neben die Liste, die man täglich ansieht.
         *
         * Das **Anlegen** bleibt hier: Ohne Verknüpfung zeigt diese Seite
         * ohnehin nur einen Leerzustand, und dort ist das Verknüpfen die
         * einzige sinnvolle Handlung.
         */
      >
        Lieferung
      </Seitenkopf>

      {/* Zahlenband. */}
      <Zahlenband className="mt-7">
        <Kennzahl
          label="MRs offen"
          grad={56}
          wert={offeneMrs}
          ton={offeneMrs > 0 ? "akzent" : undefined}
        />
        {/*
          „Läufe", nicht „Pipelines": Die Zahl zählt Durchläufe. Es gibt eine
          Pipeline je Projekt — 87-mal ausgeführt.
        */}
        <Kennzahl
          label="Läufe"
          grad={56}
          wert={String(pipelines.eintraege.length)}
        />
        <div className="flex min-w-[280px] flex-1 flex-wrap gap-x-[22px] gap-y-1.5 pb-2">
          <Nebenwert
            wert={fehlgeschlagene}
            label="fehlgeschlagen"
          />
          <Nebenwert
            wert={commits.eintraege.length}
            label="Commits"
          />
          <Nebenwert
            wert={auslieferungen.eintraege.length}
            label="Auslieferungen"
          />
        </div>
      </Zahlenband>

      {/* Der Bestätigungsdialog zum Lösen sitzt jetzt in Einstellungen → GitLab. */}
      {fehler ? (
        <p className="mt-4 text-sm text-red-600" role="alert">
          {fehler}
        </p>
      ) : null}

      {/* Reiter mit Tabmarke. */}
      <Reiterleiste className="mt-8">
        {(
          [
            "merge_requests",
            "pipelines",
            "commits",
            "auslieferungen",
            "unzugeordnete",
          ] as const
        ).map((name) => (
          <Reiter
            key={name}
            aktiv={aktiverBereich === name}
            onKlick={() => setzeAktivenBereich(name)}
          >
            {BEREICH_LABEL[name]}
          </Reiter>
        ))}
      </Reiterleiste>

      {aktiverBereich === "merge_requests" ? (
        <div className="mt-7">
          <PaginierterBereich
            daten={mrs.eintraege}
            merkschluessel="lieferung:merge-requests"
          >
            {(sichtbar) => (
              <MrAbschnitt
                projektId={projektId}
                mrs={sichtbar}
                isLoading={mrs.isLoading}
                isError={mrs.isError}
                erneut={() => void mrs.refetch()}
              />
            )}
          </PaginierterBereich>
          <MehrLaden
            hatMehr={mrs.hasNextPage}
            laedt={mrs.isFetchingNextPage}
            laden={() => void mrs.fetchNextPage()}
            beschriftung="Weitere Merge Requests laden"
          />
        </div>
      ) : null}

      {aktiverBereich === "pipelines" ? (
        <div className="mt-7">
          <PaginierterBereich
            daten={pipelines.eintraege}
            merkschluessel="lieferung:pipelines"
          >
            {(sichtbar) => (
              <PipelineAbschnitt
                projektId={projektId}
                pipelines={sichtbar}
                isLoading={pipelines.isLoading}
                isError={pipelines.isError}
                erneut={() => void pipelines.refetch()}
              />
            )}
          </PaginierterBereich>
          <MehrLaden
            hatMehr={pipelines.hasNextPage}
            laedt={pipelines.isFetchingNextPage}
            laden={() => void pipelines.fetchNextPage()}
            beschriftung="Ältere Pipelines laden"
          />
        </div>
      ) : null}

      {aktiverBereich === "commits" ? (
        <div className="mt-7">
          {commits.isLoading ? (
            <Laedt>Lade Commits …</Laedt>
          ) : commits.isError ? (
            <Fehler
              text="Commits konnten nicht geladen werden."
              erneut={() => void commits.refetch()}
            />
          ) : commits.eintraege.length === 0 ? (
            <Leer>
              Noch keine Commits gespeichert. Commits erscheinen hier, sobald
              ein produktiver Zufluss sie an cubic studio übergibt.
            </Leer>
          ) : (
            <PaginierterBereich
              daten={commits.eintraege}
              merkschluessel="lieferung:commits"
            >
              {(sichtbar) => (
                <ul role="list">
                  {sichtbar.map((commit) => (
                    <CommitZeile
                      key={commit.id}
                      commit={commit}
                      /*
                        Die MR-Liste ist ohnehin geladen; daraus wird die
                        GitLab-Nummer aufgelöst, statt die interne Kennung zu
                        zeigen, mit der niemand etwas anfangen kann.
                      */
                      mergeRequestIid={
                        mrs.eintraege.find((mr) => mr.id === commit.mergeRequestId)
                          ?.gitlabIid ?? null
                      }
                    />
                  ))}
                </ul>
              )}
            </PaginierterBereich>
          )}
          <MehrLaden
            hatMehr={commits.hasNextPage}
            laedt={commits.isFetchingNextPage}
            laden={() => void commits.fetchNextPage()}
            beschriftung="Ältere Commits laden"
          />
        </div>
      ) : null}

      {aktiverBereich === "auslieferungen" ? (
        <div className="mt-7">
          {auslieferungen.isLoading ? (
            <Laedt>Lade Auslieferungen …</Laedt>
          ) : auslieferungen.isError ? (
            <Fehler
              text="Auslieferungen konnten nicht geladen werden."
              erneut={() => void auslieferungen.refetch()}
            />
          ) : auslieferungen.eintraege.length === 0 ? (
            <Leer>
              Noch keine Auslieferungen gespeichert. Ein produktiver Zufluss
              kann noch fehlen.
            </Leer>
          ) : (
            <PaginierterBereich
              daten={auslieferungen.eintraege}
              merkschluessel="lieferung:auslieferungen"
            >
              {(sichtbar) => (
                <ul role="list">
                  {sichtbar.map((auslieferung) => (
                    <li
                      key={auslieferung.id}
                      className="flex h-[50px] flex-wrap items-center gap-3 border-b border-linie"
                    >
                      <span className="min-w-0 truncate text-[13.5px] text-tinte">
                        {auslieferung.name}
                      </span>
                      {auslieferung.version ? (
                        <span className="font-mono text-[11.5px] text-tinte4">
                          {auslieferung.version}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </PaginierterBereich>
          )}
          <MehrLaden
            hatMehr={auslieferungen.hasNextPage}
            laedt={auslieferungen.isFetchingNextPage}
            laden={() => void auslieferungen.fetchNextPage()}
            beschriftung="Ältere Auslieferungen laden"
          />
        </div>
      ) : null}

      {aktiverBereich === "unzugeordnete" ? (
        <div className="mt-7">
          {unzugeordnete.isLoading ? (
            <Laedt>Lade unzugeordnete Merge Requests …</Laedt>
          ) : unzugeordnete.isError ? (
            <Fehler
              text="Unzugeordnete Merge Requests konnten nicht geladen werden."
              erneut={() => void unzugeordnete.refetch()}
            />
          ) : unzugeordnete.eintraege.length === 0 ? (
            <Leer>Alle Merge Requests sind einer Aufgabe zugeordnet.</Leer>
          ) : (
            <PaginierterBereich
              daten={unzugeordnete.eintraege}
              merkschluessel="lieferung:unzugeordnete"
            >
              {(sichtbar) => (
                <ul role="list">
                  {sichtbar.map((mr) => {
                    const webUrl = sichereHttpUrl(mr.webUrl);
                    return (
                      <li
                        key={mr.id}
                        className="flex h-[50px] flex-wrap items-center gap-3 border-b border-linie"
                      >
                        {mr.gitlabIid ? (
                          <span className="font-mono text-[11.5px] tabular-nums text-tinte4">
                            !{mr.gitlabIid}
                          </span>
                        ) : null}
                        <span className="min-w-0 flex-1 truncate text-[13.5px] text-tinte">
                          {mr.titel || "Unvollständige ältere GitLab-Nutzlast"}
                          {mr.hinweis ? ` · ${mr.hinweis}` : ""}
                        </span>
                        {webUrl ? (
                          <a
                            className={cx(
                              "shrink-0 font-mono text-[11.5px] text-akzent hover:underline",
                              focusRing,
                            )}
                            href={webUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            GitLab öffnen
                          </a>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </PaginierterBereich>
          )}
          <MehrLaden
            hatMehr={unzugeordnete.hasNextPage}
            laedt={unzugeordnete.isFetchingNextPage}
            laden={() => void unzugeordnete.fetchNextPage()}
            beschriftung="Weitere Merge Requests laden"
          />
        </div>
      ) : null}
    </div>
  );
}

function sichereHttpUrl(wert: string): string | null {
  try {
    const url = new URL(wert);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}
